import React from 'react'
import CtaBanner, {CtaBannerType} from '../../../components/ctaBanner/components/CtaBanner';
import FooterContainer from '../../../components/footer/components/FooterContainer';
import AccuritySeoMeta, {AccuritySeoMetaType} from '../../../components/AccuritySeoMeta';
import {CustomerStoryType, StoryblokButtonType, StoryblokLinkType} from '../../../types/storyblokTypes';
import {graphql, useStaticQuery} from 'gatsby'
import {createResourceCardElement} from '../../../components/resourceCardContainer/utils';
import BackgroundLines from '../../../components/backgroundLines/components/BackgroundLines';
import {StorybookHeaderBackgroundEnum, UniversalHeader} from "../../../components/header/components/UniversalHeader";
import {uniqBy} from "lodash";

const isProduction = process.env.NODE_ENV === 'production';

type Props = {
  seoMeta: AccuritySeoMetaType;
  featuredStory: { content: CustomerStoryType, uuid: string };
  featuredStoryTag: string;
  readStoryBtnLabel: string;
  ctaBanner: { content: CtaBannerType };
  title: string;
};

type StoryblokNode = { full_slug: string, published_at: string | null, uuid: string, content: string }

const useLoadCustomerStories = (): {
  full_slug: string,
  published_at: string | null,
  uuid: string,
  content: CustomerStoryType
}[] => {
  let queryResult: { allStoryblokEntry: { nodes: StoryblokNode[] } } | undefined = undefined;
  try {
    queryResult = useStaticQuery(
      graphql`
      query LoadCustomerStories {
        allStoryblokEntry(filter: {
          field_component: {eq:"Customer Story pages"}
        }) {
          nodes{
            full_slug
            published_at
            content
            uuid
          }
        }
      }
    `
    );
  } catch {
    console.error('Static Query for Customer stories Overview failed');
  }

  return (queryResult?.allStoryblokEntry.nodes ?? []).map(node => {
    return {
      ...node,
      content: JSON.parse(node.content)
    };
  });
};

export const CustomerStoriesOverviewPage = ({
                                              seoMeta,
                                              ctaBanner,
                                              featuredStoryTag,
                                              featuredStory,
                                              title,
                                              readStoryBtnLabel,
                                            }: Props) => {
  const customerStories = useLoadCustomerStories();
  // show only published customer stories in production. In development, we can see all stories
  const sortedCustomerStories = customerStories
    .filter(q => isProduction ? q.published_at !== null : true)
    .filter(q => q.uuid !== featuredStory?.uuid)
    .sort((a, b) => {
      // unpublished first
      if (!a.published_at && b.published_at) return -1;
      if (a.published_at && !b.published_at) return 1;

      // both published → sort by date (newest first)
      if (a.published_at && b.published_at) {
        return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
      }

      return 0; // both unpublished → keep original order
    });
  const featuredCustomerStory = customerStories.find(q => q.uuid === featuredStory?.uuid) || sortedCustomerStories[0];
  const otherCustomerStories = uniqBy([featuredCustomerStory, ...sortedCustomerStories], story => story.uuid);

  const featuredStoryLink: StoryblokLinkType = {
    cached_url: featuredCustomerStory.full_slug,
    linktype: 'story',
    url: '',
    id: ''
  };

  const ctaButton: StoryblokButtonType | undefined = readStoryBtnLabel ? {
    label: readStoryBtnLabel,
    link: featuredStoryLink,
    severity: 'tertiary'
  } : undefined;

  return (
    <div className={'blogOverviewPage'}>
      <AccuritySeoMeta {...seoMeta} />

      {featuredCustomerStory && <UniversalHeader
        {...featuredCustomerStory.content.header[0]}
        tag={featuredStoryTag}
        ctaButtons={ctaButton ? [ctaButton] : undefined}
        className="c-featuredBlogPostHero"
        background={StorybookHeaderBackgroundEnum.BACKGROUND_2}
      />}

      <div className="blogOverviewPage__blogPosts container">
        <h2>{title}</h2>
        <div className="blogOverviewPage__cardsContainer">
          {otherCustomerStories.map(createResourceCardElement)}
        </div>
      </div>

      <BackgroundLines
        images={{
          'right': 'https://a.storyblok.com/f/91820/x/9951bf3d03/acc-blog-overview-lines-01.svg',
          'left': 'https://a.storyblok.com/f/91820/x/f0b29d97b0/acc-resource-center-lines-02.svg',
        }}
      />

      <CtaBanner banner={ctaBanner?.content}/>

      <FooterContainer/>
    </div>
  )
};
